import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { API_ORIGIN } from '../constants'
import { authorizedFetch } from '@/lib/api'

export interface OrganisationMember {
  id: string
  email: string
  full_name: string
  is_self: boolean
  avatar_url?: string | null
}

export interface OrganisationInvitation {
  id: string
  email: string
  expires_at: string
  created_at: string
  invited_by_email: string
  /** True while the invite request is in flight (optimistic row). */
  isOptimistic?: boolean
}

function teamQueryKey(slug: string, resource: 'members' | 'invitations') {
  return ['organisation-team', slug, resource] as const
}

export function useOrganisationTeam(slug: string | undefined) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const enabled = !!slug && !!token

  const membersQuery = useQuery({
    queryKey: teamQueryKey(slug ?? '', 'members'),
    queryFn: async (): Promise<OrganisationMember[]> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug!)}/members/`,
      )
      if (!response.ok) {
        throw new Error('Failed to fetch organisation members')
      }
      return response.json()
    },
    enabled,
  })

  const invitationsQuery = useQuery({
    queryKey: teamQueryKey(slug ?? '', 'invitations'),
    queryFn: async (): Promise<OrganisationInvitation[]> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug!)}/invitations/`,
      )
      if (!response.ok) {
        throw new Error('Failed to fetch organisation invitations')
      }
      return response.json()
    },
    enabled,
  })

  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug!)}/invitations/`,
        {
          method: 'POST',
          body: JSON.stringify({ email }),
        },
      )
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation')
      }
      return data
    },
    onMutate: async (email) => {
      if (!slug) return { previous: undefined as OrganisationInvitation[] | undefined }

      const invitationsKey = teamQueryKey(slug, 'invitations')
      await queryClient.cancelQueries({ queryKey: invitationsKey })
      const previous = queryClient.getQueryData<OrganisationInvitation[]>(invitationsKey)
      const optimistic: OrganisationInvitation = {
        id: `optimistic-${email}`,
        email,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date().toISOString(),
        invited_by_email: '',
        isOptimistic: true,
      }
      queryClient.setQueryData<OrganisationInvitation[]>(invitationsKey, (current) => {
        const existing = current ?? []
        if (existing.some((inv) => inv.email.toLowerCase() === email.toLowerCase())) {
          return existing
        }
        return [...existing, optimistic]
      })
      return { previous }
    },
    onError: (_error, _email, context) => {
      if (slug && context?.previous !== undefined) {
        queryClient.setQueryData(teamQueryKey(slug, 'invitations'), context.previous)
      }
    },
    onSettled: () => {
      if (slug) {
        void queryClient.invalidateQueries({ queryKey: teamQueryKey(slug, 'invitations') })
      }
    },
  })

  const revokeInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug!)}/invitations/${invitationId}/`,
        { method: 'DELETE' },
      )
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to revoke invitation')
      }
      return response.json()
    },
    onSuccess: () => {
      if (slug) {
        queryClient.invalidateQueries({ queryKey: teamQueryKey(slug, 'invitations') })
      }
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (memberUserId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug!)}/members/${memberUserId}/`,
        { method: 'DELETE' },
      )
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove member')
      }
      return data
    },
    onSuccess: () => {
      if (slug) {
        queryClient.invalidateQueries({ queryKey: teamQueryKey(slug, 'members') })
        queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
      }
    },
  })

  return {
    members: membersQuery.data ?? [],
    invitations: invitationsQuery.data ?? [],
    isLoadingMembers: membersQuery.isLoading,
    isLoadingInvitations: invitationsQuery.isLoading,
    membersError: membersQuery.error,
    invitationsError: invitationsQuery.error,
    inviteMember: inviteMutation.mutate,
    inviteMemberAsync: inviteMutation.mutateAsync,
    isInviting: inviteMutation.isPending,
    revokeInvitation: revokeInvitationMutation.mutate,
    isRevokingInvitation: revokeInvitationMutation.isPending,
    removeMember: removeMemberMutation.mutate,
    isRemovingMember: removeMemberMutation.isPending,
  }
}
